import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { SOURCE } from '../NeedInfoV2Constants';
import { WRITE } from '../../MagicUpload/Utils/MagicUploadConstants';
import { SendNeedInfoReminder } from '../../MagicUpload/MagicUploadHelper';
import { toast } from 'react-toastify';
import { updateQueryParams } from '../../../utils/searchParamUtils';
import { fetchUserFromOrgId } from '../../../store/investor/investorApiHelper';
import { InternalSummonUserType } from '../../../store/user/actions';
import { useAppSelector } from '../../../app/hooks';
import { Mixpanel } from '../../../utils/mixpanel';
import { getSubdomain } from '../../../utils/utils';
import { AICA_DATA_PENDING_REMINDER } from '../../../utils/constants/mixpanelEvents/investorEvents';
import { fetchCombinedOrgUsers } from '../../../store/investor/apiHelpers';

interface CombinedOrgUser {
    userId: string;
    emailId?: string;
    name: string;
    mobileNumber?: string;
    userType?: string;
    organizationId?: string;
    phoneVerified?: boolean;
    emailVerified?: boolean;
    countryCode?: string;
}

interface CombinedOrgUserList {
    assesseeUsers: CombinedOrgUser[];
    partnerUsers: CombinedOrgUser[];
}

const useDrawerControls = (setLastUpdatedAt: (lastUpdatedAt: string) => void) => {
    const platformUserId = sessionStorage.getItem('platform_user_id') ?? localStorage.getItem('platform_user_id');
    const user = useAppSelector((state) => state.user.userData);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [userList, setUserList] = useState<{ [key: string]: string }[]>([]);
    const [combinedOrgUserList, setCombinedOrgUserList] = useState<CombinedOrgUserList>({ assesseeUsers: [], partnerUsers: [] });

    const [communicationMethod, setCommunicationMethod] = useState<{
        [key: string]: boolean;
    }>({
        EMAIL: true,
    });
    const params = useParams();

    const closeDrawer = () => {
        setTimeout(() => {
            searchParams.delete(SOURCE.LENDER_REQ);
            searchParams.delete(SOURCE.BORROWER_REQ);
            searchParams.delete('dealId');
            searchParams.delete('readOnly');
            setSearchParams(searchParams);
        }, 500);
        setIsDrawerOpen(false);
    };
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        if (
            (searchParams.get(SOURCE.LENDER_REQ) === 'true' && searchParams.get('dealId')) ||
            searchParams.get(SOURCE.BORROWER_REQ) === 'true'
        )
            setIsDrawerOpen(true);
    }, [searchParams]);
    const resetCommunicationMethods = () => {
        const resetState = {};
        Object.keys(communicationMethod).forEach((key) => {
            resetState[key] = false;
        });
        setCommunicationMethod(resetState);
        setLoading(false);
    };

    const sendNotification = async (selectedReq: Set<string>) => {
        setLoading(true);
        // Give me an array where all the selectedReq ids are present who's status is 'PENDING'
        const payload = {
            assesseeOrgId: params.id as string,
            notificationMediums: Object.keys(communicationMethod).filter((key) => communicationMethod[key]),
            notificationMode: WRITE,
            notificationSenderUserId: platformUserId,
            parentOrgId: user.investorOrgId,
            selectedRequirements: Array.from(selectedReq),
        };
        await SendNeedInfoReminder(payload, {
            onSuccess: () => {
                toast.success('Reminder sent successfully');
                updateQueryParams(
                    searchParams,
                    setSearchParams,
                    {
                        disableSendReminder: true,
                    },
                    location,
                );
                resetCommunicationMethods();
                setLastUpdatedAt(dayjs().toString());
            },
            onError: () => {
                toast.error('Failed to send reminder');
                resetCommunicationMethods();
            },
        });

        Mixpanel.track(AICA_DATA_PENDING_REMINDER, {
            userRole: user?.accountType,
            pnoPlatform: getSubdomain(),
            pnoId: user?.investorOrgId,
            userId: user?.userId,
            currentUrl: window.location.href,
            organizationId: params.id ?? searchParams.get('assesseeId'),
        });
    };

    const getOrgUserList = async () => {
        const res = await fetchUserFromOrgId(
            (params.id as string) || (searchParams.get('orgId') as string),
            InternalSummonUserType.ASSESSEE,
            true,
        );
        if (res) {
            setUserList(res);
        }
    };

    const getCombinedOrgUserList = async (partnerOrgIdOverride?: string) => {
        try {
            const res = await fetchCombinedOrgUsers({
                companyOrgId: params.id ?? searchParams.get('orgId') ?? ('' as string),
                partnerOrgId: partnerOrgIdOverride ?? searchParams.get('partnerOrgId') ?? ('' as string),
            });

            if (res) {
                setCombinedOrgUserList(res.currentUsers);
            } else {
                setCombinedOrgUserList({ assesseeUsers: [], partnerUsers: [] });
            }
        } catch (error) {
            console.error('Error fetching combined organization users:', error);
            setCombinedOrgUserList({ assesseeUsers: [], partnerUsers: [] });
        }
    };

    const fetchNeedInfoList = async (selectedReq: Set<string>): Promise<any[]> => {
        const payload = {
            assesseeOrgId: params.id ?? user?.assesseeOrgId ?? searchParams.get('orgId') ?? ('' as string),
            notificationMediums: ['EMAIL'],
            notificationMode: 'READ',
            notificationSenderUserId: platformUserId,
            parentOrgId: user.investorOrgId,
            selectedRequirements: Array.from(selectedReq),
        };

        try {
            const response = await SendNeedInfoReminder(payload, { onSuccess: () => {} });
            return response?.needInfoTemplateParameters?.dataGapList || [];
        } catch (error) {
            console.error('Error fetching Need Info List:', error);
            return [];
        }
    };
    const formatDataGapList = (dataGapList: any[]) => {
        const formattedItems = dataGapList
            .filter((item) => item?.primaryMessage?.trim())
            .map((item) => {
                const primary = item.primaryMessage.trim();
                let secondary = item.secondaryMessage?.trim();

                if (secondary?.includes('${LINK}')) {
                    secondary = secondary.replace('${LINK}', item.link ?? '');
                }
                return `• ${primary}${secondary ? ` - ${secondary}` : ''}`;
            });

        return `List of pending data and clarifications:\n\n${formattedItems.join('\n')}`;
    };

    useEffect(() => {
        getOrgUserList();
        getCombinedOrgUserList();
    }, [params.id]);
    return {
        isDrawerOpen,
        closeDrawer,
        setCommunicationMethod,
        communicationMethod,
        sendNotification,
        userList,
        loading,
        getOrgUserList,
        fetchNeedInfoList,
        formatDataGapList,
        combinedOrgUserList,
        getCombinedOrgUserList,
    };
};

export default useDrawerControls;
